import type { OrganizationRoleResponse } from "@kontextwerk/control-web-client"
import type { ColumnDef, DataRow } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
import {
  roleKindLabel,
  roleLabel,
  type RoleKind,
} from "../../../../../_shared/_logic/role-presentation"

export type RoleRow = DataRow & {
  id: string
  roleId: string
  name: string
  kind: RoleKind
  kindLabel: string
  members: number
}

const KIND_ORDER: Record<RoleKind, number> = { owner: 0, administrator: 1, employee: 2, custom: 3 }
const collator = new Intl.Collator("de", { sensitivity: "base", numeric: true })

/** System roles first in their fixed order, then own roles alphabetically. */
export const buildRoleRows = (
  roles: readonly OrganizationRoleResponse[],
  memberCounts: ReadonlyMap<string, number>,
): RoleRow[] =>
  roles
    .map((role) => ({
      id: `role:${role.id}`,
      roleId: role.id,
      name: roleLabel(role) ?? role.name,
      kind: role.kind,
      kindLabel: roleKindLabel(role.kind),
      members: memberCounts.get(role.id.toLowerCase()) ?? 0,
    }))
    .sort(
      (left, right) =>
        KIND_ORDER[left.kind] - KIND_ORDER[right.kind] || collator.compare(left.name, right.name),
    )

export const filterRoleRows = (rows: readonly RoleRow[], query: string): RoleRow[] => {
  const needle = query.trim().toLowerCase()

  if (!needle) return [...rows]

  return rows.filter(
    (row) =>
      row.name.toLowerCase().includes(needle) || row.kindLabel.toLowerCase().includes(needle),
  )
}

export const roleRowsSignature = (rows: readonly RoleRow[]): string =>
  JSON.stringify(rows.map((row) => [row.id, row.name, row.kind, row.members]))

export const ROLE_COLUMNS: ColumnDef[] = [
  {
    id: "name",
    label: "Name",
    isPrimary: true,
    sortable: true,
    sortKey: "name",
    sortType: "text",
  },
  {
    id: "kindLabel",
    label: "Art",
    sortable: true,
    sortKey: "kind",
    sortType: "number",
    getSortValue: (row) => KIND_ORDER[(row as RoleRow).kind],
  },
  {
    id: "members",
    label: "Mitglieder",
    align: "right",
    sortable: true,
    sortKey: "members",
    sortType: "number",
  },
]
